import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  BookmarkCheck, FolderKanban, Sparkles, Bookmark, Archive,
  MessageSquare, Trash2, ExternalLink, FolderPlus,
} from 'lucide-react';
import { EmptyState } from '@/components/EmptyState';
import { AriHint } from '@/components/AriHint';
import { SaveToCollectionModal } from '@/components/SaveToCollectionModal';
import { fmtCurrency, fmtDistance, opportunityMeta } from '@/lib/format';
import { VAULT } from '@/constants/testIds';
import Collections from '@/pages/Collections';
import ResultsFeed from '@/pages/ResultsFeed';
import History from '@/pages/History';

/**
 * Aug 2026 §11 / §17 — Vault as memory center.
 *
 * Vault answers ONE question: what has Zita remembered/saved for me?
 * Rules and Alerts do NOT live here — they belong in Seek (Rules change
 * how Ari searches; Alerts are live intelligence for the active workspace).
 * See Home/Seek for those surfaces.
 */

const TABS = [
  { key: 'saved', label: 'Saved Listings', icon: BookmarkCheck },
  { key: 'collections', label: 'Collections', icon: FolderKanban },
  { key: 'results', label: 'Result History', icon: Sparkles },
  { key: 'history', label: 'Scans & Loops', icon: Bookmark },
  { key: 'conversations', label: 'Conversations', icon: MessageSquare },
  { key: 'archived', label: 'Archived', icon: Archive },
];

const Vault = () => {
  const { tab } = useParams();
  const navigate = useNavigate();
  const activeKey = TABS.find((t) => t.key === tab) ? tab : 'saved';

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6 space-y-6">
      <header>
        <h1 className="font-serif text-4xl text-zita-espresso">Vault</h1>
        <p className="mt-2 text-zita-mocha">Everything Zita remembers for you — saved, organized, watched.</p>
      </header>

      <AriHint
        featureKey="vault-memory-center"
        title="This is your memory center"
        body="Everything I've found for you lives here. Saved listings, collections, past scans, active Loop Scans, rules that shape your searches, and every conversation we've had — all in one place, all yours."
      />

      <div className="border-b border-zita-taupe/60 overflow-x-auto zita-scroll" data-testid="vault-tabs">
        <nav className="flex gap-1 min-w-max">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = t.key === activeKey;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => navigate(`/vault/${t.key}`)}
                data-testid={`vault-tab-${t.key}`}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 transition -mb-px ${
                  active
                    ? 'border-zita-cocoa text-zita-espresso font-semibold'
                    : 'border-transparent text-zita-mocha hover:text-zita-espresso'
                }`}
              >
                <Icon size={15} strokeWidth={1.9} />
                {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div>
        {activeKey === 'saved' && <SavedListings />}
        {activeKey === 'collections' && <Collections />}
        {activeKey === 'results' && <ResultsFeed />}
        {activeKey === 'history' && <History />}
        {activeKey === 'conversations' && <Conversations />}
        {activeKey === 'archived' && <ArchivedScans />}
      </div>
    </div>
  );
};

const SavedListings = () => {
  const [items, setItems] = useState([]);
  const [savingAll, setSavingAll] = useState(false);
  const [savingItem, setSavingItem] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/saved');
      setItems(data.saved || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    await api.patch(`/saved/${id}`, { status });
    load();
  };
  const del = async (id) => {
    await api.delete(`/saved/${id}`);
    toast.success('Removed from vault');
    load();
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-serif text-2xl text-zita-espresso">Saved listings</h2>
        {items.length > 0 && (
          <button
            type="button"
            onClick={() => setSavingAll(true)}
            data-testid="vault-save-all-to-collection"
            className="zita-btn-primary text-sm inline-flex items-center gap-1"
          >
            <FolderPlus size={13} /> Save all {items.length} to a collection
          </button>
        )}
      </div>
      <div data-testid={VAULT.list} className="space-y-3">
        {items.length === 0 ? (
          <EmptyState icon={BookmarkCheck} title="No saved listings yet" body="Save promising finds from any scan to keep track here." />
        ) : (
          items.map((s) => {
            const l = s.listing_snapshot;
            const op = l.opportunity || {};
            const opMeta = opportunityMeta(op.label);
            return (
              <div key={s.id} data-testid={VAULT.item} className="zita-card p-5 flex flex-col sm:flex-row gap-4 items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-serif text-lg text-zita-espresso">{l.title}</h3>
                    {op.label && <span className={`text-xs px-2 py-0.5 rounded-full ${opMeta.klass}`}>{opMeta.text}</span>}
                  </div>
                  <div className="text-sm text-zita-mocha mt-1 flex flex-wrap gap-x-3 gap-y-1">
                    <span>{l.is_free ? 'Free' : fmtCurrency(l.price)}</span>
                    <span>{l.source_name}</span>
                    {l.distance_miles != null && <span>{fmtDistance(l.distance_miles)}</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <a href={l.external_url} target="_blank" rel="noopener noreferrer" className="zita-btn-secondary text-xs inline-flex items-center gap-1">
                      Open <ExternalLink size={12} />
                    </a>
                    <button
                      type="button"
                      onClick={() => setSavingItem(l)}
                      data-testid="vault-item-save-to-collection"
                      className="zita-btn-secondary text-xs inline-flex items-center gap-1"
                    >
                      <FolderPlus size={12} /> Collection
                    </button>
                    <select
                      value={s.status}
                      onChange={(e) => setStatus(s.id, e.target.value)}
                      data-testid={VAULT.statusSelect}
                      className="zita-input text-xs px-2 py-1"
                    >
                      {['new','reviewing','contacted','pickup_planned','acquired','passed','sold_unavailable'].map((v) => (
                        <option key={v} value={v}>{v.replace(/_/g,' ')}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => del(s.id)}
                      data-testid={VAULT.deleteButton}
                      className="p-2 rounded-full text-zita-mocha hover:bg-zita-sand ml-auto"
                      aria-label="Delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
      {savingAll && (
        <SaveToCollectionModal
          listings={items.map((s) => ({ ...s.listing_snapshot, id: s.listing_id }))}
          searchTerm="Vault"
          onClose={() => setSavingAll(false)}
          onSaved={() => setSavingAll(false)}
        />
      )}
      {savingItem && (
        <SaveToCollectionModal
          listing={savingItem}
          onClose={() => setSavingItem(null)}
          onSaved={() => setSavingItem(null)}
        />
      )}
    </section>
  );
};

const Conversations = () => {
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/ari/conversations');
      setConvs(data.conversations || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const open = (sessionId) => {
    try { localStorage.setItem('zita-active-session', sessionId); } catch { /* ignore */ }
    navigate('/seek');
  };

  const del = async (sessionId) => {
    if (!window.confirm('Delete this conversation? Linked scans stay in Scans & Loops.')) return;
    try {
      await api.delete(`/ari/conversations/${encodeURIComponent(sessionId)}`);
      toast.success('Conversation deleted');
      load();
    } catch { toast.error('Could not delete'); }
  };

  const startNew = () => {
    try { localStorage.removeItem('zita-active-session'); } catch { /* ignore */ }
    navigate('/seek');
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-serif text-2xl text-zita-espresso">Conversations</h2>
        <button
          type="button"
          onClick={startNew}
          data-testid="vault-new-conversation"
          className="zita-btn-primary text-sm"
        >
          New conversation
        </button>
      </div>
      <AriHint
        featureKey="vault-conversations"
        title="Each conversation is a working thread"
        body="When you talk to me about a search, I remember every turn — so when you say 'the second one' or 'run that again' three days later, I still know what you meant. Open any conversation to keep going where we left off."
      />
      {loading ? (
        <div className="text-sm text-zita-mocha">Loading…</div>
      ) : convs.length === 0 ? (
        <EmptyState icon={MessageSquare} title="No conversations yet" body="Start a new one from Seek." />
      ) : (
        <div className="space-y-2">
          {convs.map((c) => (
            <div key={c.session_id} data-testid={`vault-conversation-${c.session_id}`} className="zita-card p-4 flex items-start gap-3">
              <div className="w-9 h-9 rounded-full bg-ari flex items-center justify-center text-white shrink-0">
                <Sparkles size={14} />
              </div>
              <button type="button" onClick={() => open(c.session_id)} className="flex-1 text-left min-w-0">
                <div className="font-medium text-zita-espresso truncate">{c.title}</div>
                <div className="text-xs text-zita-mocha mt-1 flex flex-wrap gap-x-3">
                  <span>{c.message_count} message{c.message_count !== 1 ? 's' : ''}</span>
                  {c.scan_ids?.length > 0 && (
                    <span>· {c.scan_ids.length} linked scan{c.scan_ids.length !== 1 ? 's' : ''}</span>
                  )}
                  {c.last_activity && (
                    <span>· last active {new Date(c.last_activity).toLocaleString()}</span>
                  )}
                </div>
              </button>
              <button
                type="button"
                onClick={() => del(c.session_id)}
                data-testid={`vault-conversation-${c.session_id}-delete`}
                aria-label="Delete conversation"
                className="p-2 rounded-full text-zita-mocha hover:bg-zita-sand shrink-0"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

const ArchivedScans = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/scans/history?kind=all&limit=200');
      setItems((data.scans || []).filter((s) => s.archived));
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <section className="space-y-3">
      <h2 className="font-serif text-2xl text-zita-espresso">Archived</h2>
      {loading ? (
        <div className="text-sm text-zita-mocha">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState icon={Archive} title="Nothing archived" body="Archived scans appear here after bulk archive from Scans & Loops." />
      ) : (
        <div className="space-y-2">
          {items.map((s) => (
            <div key={s.id} className="zita-card p-4">
              <div className="font-medium text-zita-espresso truncate">{s.command_text || 'Scan'}</div>
              <div className="text-xs text-zita-mocha">
                {(s.qualified_total ?? s.total_listings) || 0} qualified · archived {new Date(s.archived_at || s.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default Vault;
